import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { IconPlayArrow } from '../../../../../../design-system/components/icons/PlayArrow';
import { RATIO, Ratio } from '../../../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../../../design-system/components/typography/Typography';
import { HomePageDocument } from '../../../../../../resources/homePageDocument/domain/types/homePageDocument';
import { HomeIconButton } from '../../../IconButton/IconButton';

import style from './InfluencerUgcCard.module.css';

type InfluencerUgcCardProps = {
    influencerUgc: HomePageDocument['influencersUgc'][number];
    onClick: (data: { video: InfluencerUgcCardProps['influencerUgc']['video'] }) => void;
};

export function InfluencerUgcCard({ influencerUgc, onClick }: InfluencerUgcCardProps) {
    const { trackEvent } = useTrackEventAmplitude();

    const { formatMessage } = useIntl();
    const [isHovered, setIsHovered] = useState(false);

    const segmentLabel = formatMessage({
        id: `global.ugc.segment.${influencerUgc.segment}.label`,
    });

    return (
        <div
            className={style.card}
            onClick={() => {
                onClick({ video: influencerUgc.video });
            }}
            onMouseEnter={() => {
                setIsHovered(true);
            }}
            onMouseLeave={() => {
                setIsHovered(false);
            }}
            onFocus={() => {
                setIsHovered(true);
            }}
            onBlur={() => {
                setIsHovered(false);
            }}
        >
            <Ratio ratio={RATIO['6/10']} className={style.pictureWrapper}>
                <img
                    className={style.picture}
                    alt={influencerUgc.instagramName}
                    src={influencerUgc.poster.card_cover.url}
                    srcSet={`${influencerUgc.poster.card_cover_retina.url} 2x`}
                    loading="lazy"
                    sizes="220px"
                />

                <a
                    className={style.influencerLink}
                    href={influencerUgc.instagramLink.url}
                    target="_blank"
                    rel="noreferrer"
                    onMouseEnter={() => {
                        setIsHovered(false);
                    }}
                    onMouseLeave={() => {
                        setIsHovered(true);
                    }}
                    onFocus={() => {
                        setIsHovered(false);
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        trackEvent((ampli, defaultProperties) => {
                            ampli.instagramClicked({
                                ...defaultProperties,
                                block: 'social',
                                url_redirection: influencerUgc.instagramLink.url,
                            });
                        });
                    }}
                >
                    <img
                        className={style.influencerPicture}
                        alt={influencerUgc.instagramName}
                        src={influencerUgc.profilePicture.profile_picture.url}
                        srcSet={`${influencerUgc.profilePicture.profile_picture_retina.url} 2x`}
                        loading="lazy"
                        sizes="40px"
                    />
                    <Typography scale="primary-xs-regular" className={style.instagramName}>
                        {influencerUgc.instagramName}
                    </Typography>
                </a>
                <div className={style.influencerInfo}>
                    <Typography scale="primary-xxs-bold" className={style.destinationTag}>
                        {influencerUgc.destination}
                    </Typography>
                    <Typography scale="primary-xxs-bold" className={style.segmentTag}>
                        #{segmentLabel}
                    </Typography>
                </div>
                <HomeIconButton
                    className={style.playVideoButton}
                    Icon={IconPlayArrow}
                    isHovered={isHovered}
                />
            </Ratio>
        </div>
    );
}
